import { v4 as uuidv4 } from 'uuid'
import config from '@/configurations/config-environment'
import { BaseService } from '../baseService'
import importLogAdapter from '@/utils/import/importLogAdapter'

const importExportOrchestrator = config.apis.importExportOrchestrator

export const IMPORT_EXPORT_ORCHESTRATOR_ORDER_PREFIX = 'import-export-orchestrator:'

const JOB_STATUS = {
  RUNNING: 102,
  SUCCESS: 200,
  ERROR: 500
}

const OUTCOME = {
  SUCCEEDED: 'Succeeded'
}

export function toJobStatus(outcome) {
  return outcome === OUTCOME.SUCCEEDED ? JOB_STATUS.SUCCESS : JOB_STATUS.ERROR
}

export function toProjectFiles(project, fileName) {
  if (!project) return []

  return [{ projectCode: project.projectCode, projectName: project.projectName || '', filename: fileName, state: project.state }]
}

export function getProjectCodeOverride(projectFile) {
  if (!projectFile || !projectFile.newProjectCode) return undefined
  return projectFile.newProjectCode !== projectFile.projectCode ? projectFile.newProjectCode : undefined
}

export class ProjectImporter extends BaseService {
  constructor() {
    super()
    this.jobs = new Map()
  }

  createJob() {
    const orderId = `${IMPORT_EXPORT_ORCHESTRATOR_ORDER_PREFIX}${uuidv4()}`
    this.jobs.set(orderId, { jobStatus: JOB_STATUS.RUNNING, log: { records: [] } })
    return orderId
  }

  updateJob(orderId, patch) {
    const job = this.jobs.get(orderId) || {}
    this.jobs.set(orderId, { ...job, ...patch })
  }

  failJob(orderId, error) {
    console.error(error)
    this.updateJob(orderId, {
      jobStatus: JOB_STATUS.ERROR,
      log: importLogAdapter.toLogFromJson(error && error.message ? error.message : String(error))
    })
  }

  getJobStatus(orderId) {
    const job = this.jobs.get(orderId)
    return Promise.resolve(job || { jobStatus: JOB_STATUS.ERROR, log: { records: [] } })
  }

  async setConvertFile(file, token) {
    const orderId = this.createJob()

    this.runInspection(orderId, file, token)

    return orderId
  }

  async importProject(file, projectFile, token) {
    const orderId = this.createJob()

    this.runImport(orderId, file, projectFile, token)

    return orderId
  }

  async runInspection(orderId, file, token) {
    try {
      const body = this.createImportFileBody(file)
      const result = await this.post('inspect-project', body, token)

      this.updateJob(orderId, {
        jobStatus: result.project ? JOB_STATUS.SUCCESS : JOB_STATUS.ERROR,
        log: importLogAdapter.toLogFromJson(result.log),
        projectFiles: toProjectFiles(result.project, file.fileDetails.name),
        body
      })
    } catch (error) {
      this.failJob(orderId, error)
    }
  }

  async runImport(orderId, file, projectFile, token) {
    try {
      const inspection = this.jobs.get(file.conversionInformation.orderId)
      const body = {
        ...((inspection && inspection.body) || this.createImportFileBody(file)),
        projectCodeOverride: getProjectCodeOverride(projectFile)
      }
      const result = await this.post('import-project', body, token)

      this.updateJob(orderId, {
        jobStatus: toJobStatus(result.outcome),
        log: importLogAdapter.toLogFromJson(result.log)
      })
    } catch (error) {
      this.failJob(orderId, error)
    }
  }

  createImportFileBody(file) {
    const authorisation = this.getAuthorizationObject()

    return {
      inputFormatType: file.selectedImportOptions.inputFormatType,
      languageCode: this.getLangCodeLongFromShort(authorisation.Language),
      file: { name: file.fileDetails.name, contentBase64: file.base64 }
    }
  }

  async post(action, body, token) {
    const licenseNumber = this.getAuthorizationObject().Licensenumber
    const response = await fetch(`${importExportOrchestrator}/import-export/${action}/${licenseNumber}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(body)
    })

    if (!response.ok) {
      throw new Error(`Import failed. Status: ${response.status}`)
    }

    return response.json()
  }
}
